"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.INFIX_MIN_LENGTH = void 0;
exports.isWordSeparator = isWordSeparator;
exports.normalizeSearchWord = normalizeSearchWord;
exports.splitQueryTerms = splitQueryTerms;
exports.matchesInsideWords = matchesInsideWords;
exports.findQueryTermRanges = findQueryTermRanges;
exports.matchesAllQueryTerms = matchesAllQueryTerms;
exports.similarTermMaxDistance = similarTermMaxDistance;
exports.editDistance = editDistance;
exports.withinEditDistance = withinEditDistance;
exports.similarQueryMaxDistance = similarQueryMaxDistance;
exports.findSimilarTextMatches = findSimilarTextMatches;
/**
 * One word rule for every search: the server query, the server snippet, the
 * local index tokenizer and the app's highlighting split and compare words
 * the same way. Whitespace, punctuation and symbols separate words
 * ("Saatgut-Charge 7781-K" → saatgut, charge, 7781, k); words compare after
 * NFKC and lower-casing. A term of three or more characters also matches
 * inside a longer word ("affe" finds "Giraffe"); shorter terms match whole
 * words only, so "k" never marks the "k" of "known". Three equals the
 * server tables' min_infix_len.
 */
const stem_1 = require("./stem");
exports.INFIX_MIN_LENGTH = 3;
const TERM_SEPARATOR = /[\s\p{P}\p{S}]+/u;
const SEPARATOR_CHAR = /^[\s\p{P}\p{S}]$/u;
/** True for a single character (grapheme) that separates words. */
function isWordSeparator(char) {
    return SEPARATOR_CHAR.test(char);
}
/** The compared form of one word. */
function normalizeSearchWord(word) {
    return word.normalize('NFKC').toLocaleLowerCase();
}
/** Distinct normalized terms of a query, in input order. */
function splitQueryTerms(query) {
    if (typeof query !== 'string')
        return [];
    return [
        ...new Set(normalizeSearchWord(query).split(TERM_SEPARATOR).filter(Boolean)),
    ];
}
/** Whether a term also matches inside a longer word. */
function matchesInsideWords(term) {
    return [...term].length >= exports.INFIX_MIN_LENGTH;
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function termPattern(term) {
    const escaped = escapeRegExp(term);
    return new RegExp(matchesInsideWords(term)
        ? escaped
        : `(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'giu');
}
function phraseRanges(text, query) {
    const phrase = query.trim().replace(/\s+/g, ' ');
    if (!phrase || splitQueryTerms(phrase).length < 2)
        return [];
    return [...text.matchAll(new RegExp(escapeRegExp(phrase), 'giu'))].map(match => [match.index, match.index + match[0].length]);
}
/** Matched spans in reading order; the whole phrase wins over its terms. */
function findQueryTermRanges(text, query) {
    const phrase = phraseRanges(text, query);
    if (phrase.length)
        return phrase;
    const ranges = splitQueryTerms(query)
        .flatMap(term => [...text.matchAll(termPattern(term))].map((match) => [match.index, match.index + match[0].length]))
        .sort((a, b) => a[0] - b[0] || b[1] - a[1]);
    const merged = [];
    for (const range of ranges) {
        const last = merged.at(-1);
        if (last && range[0] < last[1])
            continue;
        merged.push(range);
    }
    return merged;
}
/** True when every query term occurs in `text` under the word rule. */
function matchesAllQueryTerms(text, query) {
    const terms = splitQueryTerms(query);
    return terms.length > 0 && terms.every(term => termPattern(term).test(text));
}
/** Typo budget of one word, after the same normalization as the index. */
function similarTermMaxDistance(term) {
    const length = [...(0, stem_1.foldSearchWord)(normalizeSearchWord(term))].length;
    return length < 5 ? 0 : length < 8 ? 1 : 2;
}
/**
 * Levenshtein distance in Unicode code points. With a bound, returns max + 1
 * when the distance exceeds it; only the diagonal band is evaluated.
 */
function editDistance(a, b, max = Infinity) {
    let left = [...a];
    let right = [...b];
    if (right.length > left.length)
        [left, right] = [right, left];
    const m = left.length;
    const n = right.length;
    const beyond = max + 1;
    if (m - n > max)
        return beyond;
    if (n === 0)
        return m;
    let previous = Array.from({ length: n + 1 }, (_, j) => Math.min(j, beyond));
    let current = Array(n + 1).fill(beyond);
    for (let i = 1; i <= m; i++) {
        const start = Math.max(1, i - max);
        const end = Math.min(n, i + max);
        current[0] = Math.min(i, beyond);
        if (start > 1)
            current[start - 1] = beyond;
        let best = current[0];
        for (let j = start; j <= end; j++) {
            current[j] = Math.min((previous[j] ?? beyond) + 1, (current[j - 1] ?? beyond) + 1, (previous[j - 1] ?? beyond) + (left[i - 1] === right[j - 1] ? 0 : 1));
            best = Math.min(best, current[j] ?? beyond);
        }
        if (end < n)
            current[end + 1] = beyond;
        if (best > max)
            return beyond;
        [previous, current] = [current, previous];
    }
    return Math.min(previous[n] ?? beyond, beyond);
}
/** Whether two words differ by at most the supplied typo budget. */
function withinEditDistance(a, b, max) {
    return max >= 0 && editDistance(a, b, max) <= max;
}
/** Largest typo budget in a query; zero means no similar request. */
function similarQueryMaxDistance(query) {
    return splitQueryTerms(query).reduce((max, term) => Math.max(max, similarTermMaxDistance(term)), 0);
}
/** Shared catalog/server content matcher. An exact field excludes its page. */
function findSimilarTextMatches(query, candidates) {
    const terms = splitQueryTerms(query).map(term => ({
        word: (0, stem_1.foldSearchWord)(term),
        max: similarTermMaxDistance(term),
    }));
    if (!terms.some(term => term.max > 0))
        return [];
    const matches = (words, allowEdits) => terms.every(term => words.some(word => (matchesInsideWords(term.word)
        ? word.includes(term.word)
        : word === term.word) ||
        (allowEdits &&
            term.max > 0 &&
            withinEditDistance(term.word, word, term.max))));
    const prepared = candidates.map(candidate => ({
        candidate,
        fields: candidate.texts.map(text => splitQueryTerms(text).map(stem_1.foldSearchWord)),
    }));
    const exactDocIds = new Set(prepared
        .filter(entry => entry.fields.some(words => matches(words, false)))
        .map(entry => entry.candidate.docId));
    return prepared.flatMap(({ candidate, fields }) => {
        if (exactDocIds.has(candidate.docId))
            return [];
        const matchedTextIndex = fields.findIndex(words => matches(words, true));
        return matchedTextIndex < 0 ? [] : [{ ...candidate, matchedTextIndex }];
    });
}
//# sourceMappingURL=query-terms.js.map